'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import { useNativeKeyboardHeight } from '@/lib/react-native/services/react-native.keyboard'
import { Form } from '@/ui/form/Form.client'
import { ScrollableY } from '@/ui/scrollbar/ScrollableY.client'
import { Loader } from '@/ui/loaders/loader/Loader'
import { Skin } from '@/ui/skin/Skin'
import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Dialog, DialogTrigger, Modal, ModalOverlay } from 'react-aria-components'
import { DrawerActions } from './_components/DrawerActionButtons'
import { DrawerContext } from './_components/DrawerContext'
import { DrawerHeader } from './_components/DrawerHeader'
import { useDrawerActions } from './_hooks/useDrawerActions'
import type { DrawerProps } from './drawer.types'

const drawerCva = cva('', {
  variants: {
    size: {
      xl: 'desktop:w-[38rem]',
      lg: 'desktop:w-[28rem]',
      sm: 'desktop:w-[25rem]',
    },
  },
})

export type {
  DrawerControlButtonProps,
  DrawerControlProps,
  DrawerControlStateProps,
  DrawerProps,
} from './drawer.types'

export {
  DrawerAction,
  DrawerBody,
  DrawerFooter,
  DrawerHeading,
  useCloseDrawer,
} from './_components/DrawerContext'

export function Drawer({
  children,
  className,
  trigger,
  title,
  subtitle,
  'aria-label': ariaLabel,
  upperCaseTitle: isTitleUppercase = false,
  isDismissable = true,
  isOpen,
  onOpenChange,
  size = 'lg',
  hideCloseButton = false,
  handle,
  isLoading,
  id,
  'data-testid': dataTestId = 'drawer',
  onActionSucceeded = () => {},
  onActionFailed = {},
  tint = 'body',
  onBack,
}: DrawerProps) {
  const [heading, setHeading] = useState<ReactNode>(null)
  const [body, setBody] = useState<ReactNode>(null)
  const [footer, setFooter] = useState<ReactNode>(null)
  const [isBodyScrolledDown, setIsBodyScrolledDown] = useState(false)

  const closeRef = useRef<() => void>(() => {})
  const closeDrawer = useCallback(() => {
    closeRef.current()
  }, [])

  const isInWebView = !!useIsReactNativeWebView()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const keyboardHeight = useNativeKeyboardHeight(isInWebView)
  const [bottomInset, setBottomInset] = useState(0)

  // The keyboard height not already covered by the footer needs to be reserved.
  useEffect(() => {
    if (!isInWebView) return

    const footerHeight = footerRef.current?.getBoundingClientRect().height ?? 0

    setBottomInset(Math.max(keyboardHeight - footerHeight, 0))
  }, [isInWebView, keyboardHeight])

  // Scroll the focused field above the keyboard.
  useEffect(() => {
    if (!isInWebView || !keyboardHeight) return

    const frame = requestAnimationFrame(() => {
      const activeElement = document.activeElement
      const scrollContainer = scrollContainerRef.current

      if (!(activeElement instanceof HTMLElement) || !scrollContainer) return

      const keyboardTop = window.innerHeight - keyboardHeight
      const overlap = activeElement.getBoundingClientRect().bottom - keyboardTop

      if (overlap > 0) {
        scrollContainer.scrollTop += overlap
      }
    })

    return () => cancelAnimationFrame(frame)
  }, [isInWebView, keyboardHeight, bottomInset])

  const {
    actions,
    announcementRef,
    hasActions,
    loading,
    onSubmit,
    registerAction,
    startTransition,
    unregisterAction,
  } = useDrawerActions({ close: closeDrawer, onActionSucceeded, onActionFailed })

  useEffect(() => {
    if (!handle) return

    const handleCloseDrawer = (e: MessageEvent) => {
      if (e.data?.type === 'CLOSE_DRAWER' && e.data?.handle === handle) {
        closeRef.current()
      }
    }

    window.addEventListener('message', handleCloseDrawer)

    return () => {
      window.removeEventListener('message', handleCloseDrawer)
    }
  }, [handle])

  const dialogAriaLabel = title ? undefined : ariaLabel

  const overlayContent = (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className={twMerge(
        'fixed top-0 z-drawer block h-dvh w-full group/drawer',
        'before:absolute before:inset-0 before:block before:bg-black before:opacity-40',
        'data-[entering]:animate-empty-in data-[exiting]:animate-empty-out',
        'group-data-[entering]/drawer:animate-fade-in group-data-[exiting]/drawer:animate-fade-out',
        'embed:animate-none',
      )}
      isDismissable={isDismissable && !loading}
      isKeyboardDismissDisabled={!isDismissable || loading}
    >
      <Modal className="pointer-events-none h-full">
        <Dialog
          id={id}
          data-testid={dataTestId}
          aria-label={dialogAriaLabel}
          className={twMerge(
            'pointer-events-auto relative ml-auto flex h-full flex-col overflow-hidden rounded-none shadow-picture outline-none embed:animate-none desktop:rounded-bl-md desktop:rounded-tl-md',
            'tablet:group-data-[entering]/drawer:animate-slide-right-in tablet:group-data-[exiting]/drawer:animate-slide-right-out',
            'bg-white group-data-[entering]/drawer:animate-slide-up-in group-data-[exiting]/drawer:animate-slide-up-out',
            drawerCva({ size }),
          )}
        >
          {({ close }) => {
            closeRef.current = close

            const drawerContent = (
              <Skin
                tint={tint}
                className={twMerge(
                  'flex min-h-0 w-full flex-1 flex-col overflow-hidden px-3',
                  className,
                )}
              >
                <div ref={announcementRef} role="status" aria-live="polite" className="sr-only" />

                <DrawerHeader
                  title={title}
                  subtitle={subtitle}
                  isTitleUppercase={isTitleUppercase}
                  isLoading={isLoading}
                  hideCloseButton={hideCloseButton}
                  isPending={loading}
                  heading={heading}
                  isBodyScrolledDown={isBodyScrolledDown}
                  onBack={onBack}
                />

                <ScrollableY
                  ref={scrollContainerRef}
                  className="min-h-0 flex-1"
                  onScrollDownChanged={setIsBodyScrolledDown}
                >
                  <div style={bottomInset ? { paddingBottom: bottomInset } : undefined}>
                    {isLoading ? (
                      <div
                        className="grid size-full place-items-center"
                        data-testid="drawer/loading"
                        aria-busy
                      >
                        <Loader />
                      </div>
                    ) : (
                      body
                    )}
                  </div>
                </ScrollableY>

                {(footer || (!isLoading && hasActions)) && (
                  <div ref={footerRef} className="flex shrink-0 flex-col gap-3 py-3">
                    {footer}

                    {!isLoading && hasActions && (
                      <DrawerActions
                        actions={actions}
                        isPending={loading}
                        close={close}
                        startTransition={startTransition}
                      />
                    )}
                  </div>
                )}
              </Skin>
            )

            if (actions.submit) {
              return (
                <Form onSubmitAction={onSubmit} form={actions.submit.form} className="contents">
                  {drawerContent}
                </Form>
              )
            }

            return drawerContent
          }}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )

  return (
    <DrawerContext.Provider
      value={{
        heading,
        setHeading,
        body,
        setBody,
        registerAction,
        unregisterAction,
        footer,
        setFooter,
        close: closeDrawer,
      }}
    >
      {trigger ? (
        <DialogTrigger>
          {trigger}
          {overlayContent}
        </DialogTrigger>
      ) : (
        overlayContent
      )}
      {children}
    </DrawerContext.Provider>
  )
}
